<template>
  <v-chart
    class="chart pa-6"
    :option="option"
    :update-options="{replaceMerge: 'series'}"
    :theme="$vuetify.theme.dark ? themeDark : themeLight"
    autoresize
  />
</template>

<script setup lang="ts">
import {
  PropType, ref, watch, computed,
} from 'vue';
import themeDark from '@/../static/chart-dark.json';
import themeLight from '@/../static/chart-light.json';
import i18n from '@/plugins/i18n';
import { use } from 'echarts/core';
import { FunnelChart } from 'echarts/charts';
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
} from 'echarts/components';
import VChart from 'vue-echarts';

use([
  FunnelChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
]);

const props = defineProps({
  value: { type: Number as PropType<number>, required: true },
});

const title = computed(() => {
  if (props.value > 1) return i18n.t('label.newsletter_unsubscribe_ratio_poor');
  if (props.value > 0.75) return i18n.t('label.newsletter_unsubscribe_ratio_poor');
  if (props.value > 0.5) return i18n.t('label.newsletter_unsubscribe_ratio_good');
  return i18n.t('label.newsletter_unsubscribe_ratio_excellent');
});

const createDataset = () => ({
  series: [
    {
      type: 'gauge',
      startAngle: 180,
      endAngle: 0,
      center: ['50%', '70%'],
      radius: '135%',
      min: 0,
      max: 2,
      splitNumber: 4,
      axisLine: {
        lineStyle: {
          width: 2,
          color: [
            [0.25, '#00A26F'],
            [0.50, '#ffc24e'],
            [0.75, '#FF8262'],
            [1, '#FF002B'],
          ],
        },
      },
      pointer: {
        icon: 'path://M12.8,0.7l12,40.1H0.7L12.8,0.7z',
        length: '15%',
        width: 20,
        offsetCenter: [0, '-40%'],
        itemStyle: {
          color: 'auto',
        },
      },
      axisTick: {
        length: 12,
        lineStyle: {
          color: 'auto',
          width: 2,
        },
      },
      splitLine: {
        length: 20,
        lineStyle: {
          color: 'auto',
          width: 4,
        },
      },
      axisLabel: { show: false },
      title: {
        offsetCenter: [0, '20%'],
        fontSize: 12,
        color: undefined,
      },
      detail: {
        fontSize: 18,
        offsetCenter: [0, 0],
        valueAnimation: true,
        formatter(value: number) {
          return `${Math.floor(value * 1000) / 10}%`;
        },
        color: 'inherit',
      },
      data: [
        {
          value: props.value,
          name: title.value,
        },
      ],
    },
  ],
});

const option = ref(createDataset());

const reset = () => { option.value = createDataset(); };

watch(() => props.value, () => reset());

</script>

<style scoped>

  .chart {
    height: 200px;
  }

</style>
